// La conversación entera: lista de mensajes + barra de entrada.
//
// ES EL COMPONENTE COMPARTIDO. El panel lateral y la pantalla dedicada montan este mismo
// árbol y solo le cambian el ancho de la columna y los márgenes.
//
// Las burbujas y las TARJETAS son las del asistente (`MessageRow` de `ChatPanelV2`, que
// dibuja el texto, las propuestas de generación, los pickers, la galería, el soporte y el
// "sin créditos") — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente. Debajo de cada
// respuesta van los extras del Chat: la tarjeta de un gráfico y los chips de sugerencia.
//
// Y arriba del input está el SLOT DE LA CARD ACTIVA, igual que en el asistente. No es
// decoración: `MessageRow` sólo dibuja las cards resueltas y las inline-pendientes, así
// que una card que necesita el turno vivo —`pick_product`, `confirm_research`, las de
// lanzamientos— no se dibujaba en NINGÚN lado. El turno se quedaba esperando una respuesta
// que el usuario no tenía cómo dar, sin siquiera el "pensando" (que se apaga justamente
// cuando hay card activa): el chat congelado (ADR-2026-09-09-el-chat-necesita-su-slot-de-card).

import { useEffect, useRef, useState } from 'react';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import { BarraEntradaChat } from './BarraEntradaChat';
import { type VistaGrafico } from './BurbujaMensaje';
import { InicioAgente } from './InicioAgente';
import { MensajesDeLaConversacion } from './MensajesDeLaConversacion';
import { EncuestaEnLaCaja, useEncuestaEnLaCaja } from './EncuestaEnLugarDeLaCaja';
import { ModalDeFuentes, type IdDeFuente } from './ModalDeFuentes';
import { TarjetasDeReferenciaDelChat, esAdjuntoDeTarjeta } from './TarjetasDeReferenciaDelChat';
import { SelectoresDeAdjuntos, type SelectorAbierto } from './SelectoresDeAdjuntos';
import { pestanaDeAccion, type PestanaBiblioteca } from './bibliotecaDeAdjuntos';
import { useEsEscritorio } from './useEsEscritorio';
import { usePrimeraVezEnElAgente } from './usePrimeraVezEnElAgente';

interface Props {
  /** Ancho de la columna de mensajes. La pantalla dedicada centra en una columna
   *  cómoda; el panel usa todo el ancho de la franja. */
  anchoColumna?: string;
  /** Padding lateral. */
  padding?: string;
  /** Qué hace la tarjeta de una respuesta gráfica. En el panel lateral no hay ancho
   *  para dibujarla, así que manda a la pantalla dedicada. */
  vistaGrafico?: VistaGrafico;
  /** Se llama con el id del gráfico al tocar su tarjeta. */
  onAbrirGrafico?: (id: string) => void;
  /** Panel lateral (angosto) o pantalla dedicada: cambia los tamaños del arranque y el
   *  alto del composer, no las piezas (ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2). */
  variante?: 'panel' | 'pantalla';
  /** En la pantalla dedicada de desktop, las cards del "+" que eligen del catálogo abren
   *  la BIBLIOTECA (la columna de la derecha) en su pestaña, en vez del selector modal
   *  (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente). Sin esto, el
   *  selector de siempre. */
  onAbrirBiblioteca?: (pestana: PestanaBiblioteca) => void;
  /** Las tarjetas de Crear en la caja aunque el contexto no las pida: la pantalla del Agente,
   *  que se ve como el Asistente (ADR-2026-10-16-el-agente-se-ve-como-el-asistente). */
  conTarjetas?: boolean;
}

export const ConversacionChatAtlas: React.FC<Props> = ({
  anchoColumna = 'max-w-3xl',
  padding = 'px-4 sm:px-6',
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
  variante = 'pantalla',
  onAbrirBiblioteca,
  conTarjetas: conTarjetasPedidas,
}) => {
  const chat = useChatAtlas();
  const entorno = useEntornoChatAtlas();
  const { companyId } = entorno;
  const esEscritorio = useEsEscritorio();
  // Selector del catálogo abierto (producto / modelo / pose / fondo / imagen ya
  // generada) — ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca.
  const [selector, setSelector] = useState<SelectorAbierto>(null);
  const abrirSelector = (accion: NonNullable<SelectorAbierto>) => {
    const pestana = pestanaDeAccion(accion);
    if (onAbrirBiblioteca && pestana) onAbrirBiblioteca(pestana);
    else setSelector(accion);
  };
  // El "+" abre el selector de fuentes con pestañas, el MISMO de la caja de la home, y en
  // el teléfono su hoja (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes).
  const [fuentes, setFuentes] = useState(false);
  /** La pestaña con la que abre el selector: la de la tarjeta tocada, o la de siempre (el "+"). */
  const [pestanaDeFuentes, setPestanaDeFuentes] = useState<IdDeFuente | undefined>(undefined);
  // Las tarjetas de Crear arriba de la caja, si quien abrió el chat las pidió
  // (ADR-2026-10-13-las-tarjetas-de-crear-en-el-panel-del-asistente). Lo que muestran no se
  // repite como miniatura dentro de la caja.
  const conTarjetas = conTarjetasPedidas ?? !!chat.contexto?.conTarjetasDeReferencia;
  // ¿Está escribiendo? En el arranque de mobile, el foco en la caja esconde el título y
  // muestra las sugerencias arriba de la caja. El blur se aplica con un respiro: tocar
  // una sugerencia primero le saca el foco al textarea y recién después dispara el
  // click — si las sugerencias se fueran en el acto, el click caería en la nada.
  const [escribiendo, setEscribiendo] = useState(false);
  const blurRef = useRef<number | null>(null);
  const alEnfocar = (enfocado: boolean) => {
    if (blurRef.current) {
      window.clearTimeout(blurRef.current);
      blurRef.current = null;
    }
    if (enfocado) setEscribiendo(true);
    else blurRef.current = window.setTimeout(() => setEscribiendo(false), 250);
  };
  useEffect(() => () => {
    if (blurRef.current) window.clearTimeout(blurRef.current);
  }, []);
  // Una sugerencia —del arranque o del turno— COMPLETA la caja y deja el cursor ahí; no
  // manda: el usuario retoca el pedido (su producto, la red, la duración) antes de enviarlo
  // (ADR-2026-09-12-las-sugerencias-completan-la-caja,
  // ADR-2026-10-20-las-sugerencias-del-turno-completan-la-caja). El contador le pide el foco al
  // textarea; es un número para que la misma sugerencia dos veces también enfoque.
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  const completarConSugerencia = (texto: string) => {
    chat.setBorrador(texto);
    setPedidoDeFoco((n) => n + 1);
  };

  // La lista de mensajes (con su scroll, Cumbre al pie, la línea de lo que se genera en
  // otro lado y el slot de la card activa) es `MensajesDeLaConversacion`; acá queda el
  // arranque y el composer (ADR-2026-10-12-la-conversacion-del-asistente-en-crear). Al
  // mandar, la lista vuelve a seguir la respuesta: el contador se lo avisa.
  const [pedidoDeIrAlFondo, setPedidoDeIrAlFondo] = useState(0);
  // La encuesta va en el lugar de la caja de texto; "Prefiero escribirlo" vuelve a la caja para ÉSA.
  // Con eso la caja manda en vez de encolar: `enviarMensaje` suelta la encuesta y sigue con lo nuevo
  // (ADR-2026-10-22-la-encuesta-reemplaza-la-caja-de-texto).
  const { encuesta, escribeSobreLaEncuesta, preferirEscribir } = useEncuestaEnLaCaja(chat.motor);
  const vacio = chat.mensajes.length === 0;
  // Primera vez en el Agente: en vez del arranque va la pantalla de bienvenida, hasta que
  // toque "Genial, empecemos" (ADR-2026-09-14-la-bienvenida-del-agente). Sólo se pregunta
  // con la conversación vacía, que es cuando ese arranque existe.
  // El `usuarioId` va por parámetro: la marca de "ya la cerró" es POR CUENTA
  // (ADR-2026-09-15-la-bienvenida-se-acuerda-por-cuenta).
  const bienvenida = usePrimeraVezEnElAgente(vacio, entorno.usuarioId);
  // El composer es UNO: con el chat vacío vive en el medio del arranque; con mensajes,
  // al pie. Se remonta al pasar de un lugar al otro (el borrador y los adjuntos viven en
  // el contexto, así que no se pierde nada).
  const composer = (
    <BarraEntradaChat
      valor={chat.borrador}
      onCambiarValor={chat.setBorrador}
      onEnviar={(texto) => {
        setPedidoDeIrAlFondo((n) => n + 1);
        return chat.enviarMensaje(texto);
      }}
      streaming={chat.streaming && !escribeSobreLaEncuesta}
      onDetener={chat.detener}
      cola={chat.cola}
      onEncolar={chat.encolarMensaje}
      onQuitarDeCola={chat.quitarDeCola}
      interruptores={chat.interruptores}
      onCambiarInterruptor={chat.setInterruptor}
      preferencias={chat.preferencias}
      onCambiarPreferencia={chat.setPreferencia}
      adjuntos={chat.adjuntos}
      // Con las tarjetas de Crear, lo que ellas muestran no se repite como chip; la lista
      // entera sigue yendo a la caja para la `@` y el tope (review atlas-code#179).
      ocultarChip={conTarjetas ? (a) => esAdjuntoDeTarjeta(a.kind) : undefined}
      onAgregarAdjunto={chat.agregarAdjunto}
      onQuitarAdjunto={chat.quitarAdjunto}
      adjuntosTentativos={chat.adjuntosTentativos}
      loteFotos={chat.loteFotos}
      onAdjuntarLote={chat.agregarLoteFotos}
      onQuitarLote={chat.quitarLoteFotos}
      onAbrirSelector={abrirSelector}
      companyId={companyId}
      inteligencia={chat.inteligencia}
      onCambiarInteligencia={chat.setInteligencia}
      puedeSubirInteligencia={chat.puedeSubirInteligencia}
      modeloDeImagen={chat.modeloDeImagen}
      onCambiarModeloDeImagen={chat.setModeloDeImagen}
      // En el arranque de mobile NO se enfoca sola: el título y la mascota se ven hasta
      // que el usuario toca la caja (y el teclado no salta apenas entra).
      autoFoco={esEscritorio || !vacio}
      alto={vacio && variante === 'pantalla' ? 'amplio' : 'normal'}
      // En desktop también el panel lateral y la conversación con el lienzo abierto, si
      // la fila tiene lugar (ADR-2026-09-25-agregar-con-rotulo-en-el-panel-y-con-el-lienzo).
      rotuloAgregar={variante === 'pantalla' ? true : esEscritorio ? 'si-entra' : false}
      // El "+" abre el selector de fuentes (no el menú) y la fila lleva la Inteligencia y
      // el engranaje de configuración, como la caja de la home
      // (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes).
      onAbrirFuentes={() => {
        setPestanaDeFuentes(undefined);
        setFuentes(true);
      }}
      pastillasDeContenido
      // Con las tarjetas de Crear arriba, el "+ Agregar" sobra: agregan ellas
      // (ADR-2026-10-13-sin-agregar-en-el-panel-del-asistente).
      conBotonMas={!conTarjetas}
      variante={variante}
      onEnfoque={alEnfocar}
      pedidoDeFoco={pedidoDeFoco}
      // Las tarjetas de Crear, adentro de la caja y arriba del texto, como en la caja de Crear
      // (ADR-2026-10-13-las-tarjetas-de-crear-en-el-panel-del-asistente-2).
      encabezado={
        conTarjetas ? (
          <TarjetasDeReferenciaDelChat
            onAbrir={(pestana) => {
              setPestanaDeFuentes(pestana);
              setFuentes(true);
            }}
          />
        ) : undefined
      }
    />
  );

  if (vacio) {
    return (
      <>
        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${padding} py-6`}
          style={{ scrollbarWidth: 'thin' }}
        >
          <InicioAgente
            variante={variante}
            contexto={chat.contexto?.pantalla}
            muestraCosto={chat.inteligencia !== 'baja'}
            bloqueado={chat.streaming}
            onSugerencia={completarConSugerencia}
            anchoColumna={anchoColumna}
            escribiendo={escribiendo}
            bienvenida={bienvenida.mostrar}
            onEmpezar={bienvenida.empezar}
          >
            {composer}
          </InicioAgente>
        </div>
        <ModalDeFuentes abierto={fuentes} onCerrar={() => setFuentes(false)} />
        <SelectoresDeAdjuntos
          abierto={selector}
          onCerrar={() => setSelector(null)}
          onAdjuntar={chat.agregarAdjunto}
          productos={entorno.productos}
          modelos={entorno.modelos}
          usuarioId={entorno.usuarioId}
          companyId={companyId}
          onModeloCreado={entorno.refrescarModelos}
        />
      </>
    );
  }

  return (
    <>
      <MensajesDeLaConversacion
        encuestaEnLaCaja
        anchoColumna={anchoColumna}
        padding={padding}
        vistaGrafico={vistaGrafico}
        onAbrirGrafico={onAbrirGrafico}
        pedidoDeIrAlFondo={pedidoDeIrAlFondo}
        onSugerencia={completarConSugerencia}
      />

      <div
        className={`shrink-0 ${padding} pt-3`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className={`mx-auto ${anchoColumna}`}>
          {encuesta ? (
            <EncuestaEnLaCaja
              encuesta={encuesta}
              motor={chat.motor}
              onPreferirEscribir={() => {
                preferirEscribir(encuesta.id);
                setPedidoDeFoco((n) => n + 1);
              }}
            />
          ) : (
            composer
          )}
        </div>
      </div>

      {/* Los selectores reales de la app (portaleados): producto, modelo, pose, fondo e
          imagen ya generada. Sólo se montan cuando se abren. */}
      <ModalDeFuentes abierto={fuentes} onCerrar={() => setFuentes(false)} {...(pestanaDeFuentes ? { pestanaInicial: pestanaDeFuentes } : {})} />
      <SelectoresDeAdjuntos
        abierto={selector}
        onCerrar={() => setSelector(null)}
        onAdjuntar={chat.agregarAdjunto}
        productos={entorno.productos}
        modelos={entorno.modelos}
        usuarioId={entorno.usuarioId}
        companyId={companyId}
        onModeloCreado={entorno.refrescarModelos}
      />
    </>
  );
};

export default ConversacionChatAtlas;
